import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';

import Modal from "component/Modal";
import SelectField from "component/Select/Select";
import DateIntervalInput from 'component/DateIntervalInput/NewDateIntevalInput'
import Input from "component/utils/Input";
import Col from "component/utils/Col";
import Row from "component/utils/Row";
import ParcelRange from "component/priceBanner/priceBannerParams/ParcelRange";
import RegionBranchMultipleSelect from 'component/branch/RegionBranchMultipleSelectNew'
import DynamicSelectField from 'component/priceBanner/selects/DynamicSelectField'

import 'container/priceBanner/styles.css';
import moment from "moment";

const calculateStatus = (startDate, endDate) => {
  const today = moment().startOf('day');
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  if (end.isBefore(today)) {
    return 2;
  }
  if (start.isAfter(today)) {
    return 1;
  }
  return 0;
};

export function PricebannerAddModal({ open, onOpenChange, onSave, user, translate, initialData, title }) {

  const [idBranches, setIdBranches] = useState([]);
  const [cdDynamic, setCdDynamic] = useState();
  const [dsCard, setDsCard] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [repeatYearly, setRepeatYearly] = useState(false);

  const [ranges, setRanges] = useState([{
    id: Date.now(),
    cdParcel: [],
    prMinValue: "",
    prMaxValue: "",
    cdNewDynamic: [],
  }
  ])

  const [errors, setErrors] = useState({
    branches: false,
    dynamic: false,
    cards: false,
    date: false,
    ranges: {}
  })

  if (!open) return null;

  const requiredMessage = translate('mandatory');

  useEffect(() => {
  }, [ranges]);

  useEffect(() => {

    if (!initialData) {
      setIdBranches([]);
      setCdDynamic(null);
      setDsCard(null);
      setDateRange([]);
      setRepeatYearly(false);
      setRanges([{
        id: Date.now(),
        cdParcel: null,
        prMinValue: "",
        prMaxValue: "",
        dynamic: null,
      }]);
      return;
    }

    if (initialData.branches) {
      const branches = initialData.branches.map(b => ({
        value: b.idBranch,
        label: b.dsBranch
      }));

      const regionMapping = {
        "Regional Interior SP": 7,
        "Regional Centro Nordeste": 4,
        "Regional Sudeste": 1,
        "Região SP": 3,
        "Região Sul": 2
      };

      const regions = initialData.branchNamesGrouped
        ? initialData.branchNamesGrouped.map(regionName => ({
          value: regionMapping[regionName] || regionName,
          label: regionName
        }))
        : [];

      setIdBranches([...branches, ...regions]);

    } else if (initialData.idBranches) {
      setIdBranches(initialData.idBranches.map(id => ({
        value: id,
      })));
    }

    if (initialData.dynamic) {
      setCdDynamic({
        value: initialData.dynamic.cdDynamic,
        label: initialData.dynamic.dsDynamic
      });
    }

    if (initialData.dsCard) {
      setDsCard({ value: initialData.dsCard, label: initialData.dsCard });
    }

    const startRaw = initialData.dtStart;
    const endRaw = initialData.dtEnd;
    const start = moment(startRaw, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601], true);
    const end = moment(endRaw, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601], true);
    setDateRange([start, end]);

    setRepeatYearly(!!initialData.annualRepeat);

    if (Array.isArray(initialData.parcelRangeList)) {
      const rangesFormatted = initialData.parcelRangeList.map((item, idx) => ({
        id: Date.now() + idx,
        cdParcel: item.cdParcel != null
          ? { value: item.cdParcel.toString(), label: `${item.cdParcel}x` }
          : null,
        prMinValue: parseAndFormatCurrency(item.prMinValueFormatted ?? item.prMinValue),
        prMaxValue: parseAndFormatCurrency(item.prMaxValueFormatted ?? item.prMaxValue),
        dynamic: item.dynamic
          ? { value: item.dynamic.cdDynamic, label: item.dynamic.dsDynamic }
          : null
      }));
      setRanges(rangesFormatted);
    }

  }, [initialData]);


  useEffect(() => {
    if (open) {
      setErrors({
        branches: false,
        dynamic: false,
        cards: false,
        date: false,
        ranges: {}
      });
    }
  }, [open]);


  const getUsedParcels = (currentId) => ranges
    .filter(r => r.id !== currentId)
    .map(r => r.cdParcel?.value)
    .filter(Boolean)

  const getPreviousMaxValue = (index) => {
    if (index === 0) return null;
    const previous = ranges[index - 1];
    return previous?.prMaxValue ?? null;
  }

  const parseCurrency = (value) => {
    if (typeof value === 'number') {
      return value;
    }

    if (!value) {
      return 0;
    }

    const onlyDigits = value.toString().replace(/\D/g, '');

    const amount = parseInt(onlyDigits, 10) / 100;

    return Number.isNaN(amount) ? 0 : amount;
  };

  const parseFormattedCurrency = (value) => {
    if (typeof value === 'number') {
      return value;
    }

    if (!value || value === '') {
      return 0;
    }

    const stringValue = value.toString();

    if (stringValue.includes(',')) {
      const cleanValue = stringValue
        .replace(/\./g, '')
        .replace(',', '.');

      const numberValue = parseFloat(cleanValue);
      return Number.isNaN(numberValue) ? 0 : numberValue;
    }

    const lastDotIndex = stringValue.lastIndexOf('.');
    if (lastDotIndex !== -1 && stringValue.length - lastDotIndex === 3) {
      const cleanValue = stringValue.replace(/,/g, '');
      const numberValue = parseFloat(cleanValue);
      return Number.isNaN(numberValue) ? 0 : numberValue;
    }

    const cleanValue = stringValue
      .replace(/\./g, '')
      .replace(',', '.');

    const numberValue = parseFloat(cleanValue);
    return Number.isNaN(numberValue) ? 0 : numberValue;
  };

  const formatCurrency = (num) => {
    if (typeof num !== 'number') return num;
    return num.toLocaleString('pt-BR', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  const parseAndFormatCurrency = (value) => {
    const numberValue = parseFormattedCurrency(value);
    return formatCurrency(numberValue);
  };

  const updateRange = (id, updatedData) => {
    setRanges(r =>
      r.map(x => x.id === id ? { ...x, ...updatedData } : x)
    )
    setErrors(e => {
      const nr = { ...e.ranges }
      delete nr[id]
      return { ...e, ranges: nr }
    })
  }

  const addRange = (position, id) => {
    const newRange = { id: crypto.randomUUID() }
    const index = ranges.findIndex((r) => r.id === id)

    if (index === -1) {
      return
    }

    const newRanges =
      position === 'above'
        ? [
          ...ranges.slice(0, index),
          newRange,
          ...ranges.slice(index),
        ]
        : [
          ...ranges.slice(0, index + 1),
          newRange,
          ...ranges.slice(index + 1),
        ]

    setRanges(newRanges)
  }

  const removeRange = (id) => {
    if (ranges.length === 1) return

    const newRanges = ranges.filter((r) => r.id !== id)
    setRanges(newRanges)
  }

  const handleConfirm = () => {
    const newErr = {
      branches: idBranches.length === 0,
      dynamic: !cdDynamic || !cdDynamic.value,
      cards: !dsCard || !dsCard.value,
      date: !dateRange || dateRange.length !== 2,
      ranges: {}
    };

    let hasError = newErr.branches || newErr.dynamic || newErr.cards || newErr.date;

    ranges.forEach((r, index) => {
      const err = {};

      if (!r.cdParcel?.value) err.cdParcel = requiredMessage;
      if (!r.dynamic || !r.dynamic.cdDynamic) err.dynamic = requiredMessage;

      const minNum = parseCurrency(r.prMinValue);
      const maxNum = parseCurrency(r.prMaxValue);

      const prevMaxRaw = getPreviousMaxValue(index);
      const prevMax = prevMaxRaw !== null ? parseCurrency(prevMaxRaw) : null;

      if (!r.prMinValue && r.prMinValue !== 0) {
        if (prevMax !== null) {
          err.prMinValue = `Mínimo: ${formatCurrency(prevMax + 0.01)}`;
        } else {
          err.prMinValue = requiredMessage;
        }
      } else if (prevMax !== null && minNum <= prevMax) {
        err.prMinValue = `Mínimo: ${formatCurrency(prevMax + 0.01)}`;
      }

      if (!r.prMaxValue && r.prMaxValue !== 0) {
        if (r.prMinValue || r.prMinValue === 0) {
          err.prMaxValue = `Mínimo: ${formatCurrency(minNum + 0.01)}`;
        } else if (prevMax !== null) {
          err.prMaxValue = `Mínimo: ${formatCurrency(prevMax + 0.0)}`;
        } else {
          err.prMaxValue = requiredMessage;
        }
      } else if (maxNum <= minNum) {
        err.prMaxValue = `Mínimo: ${formatCurrency(minNum + 0.01)}`;
      }

      if (Object.keys(err).length > 0) {
        newErr.ranges[r.id] = err;
        hasError = true;
      }
    });

    if (hasError) {
      setErrors(newErr);
      return;
    }

    const status = calculateStatus(dateRange[0], dateRange[1]);
    
    const payload = {
      idBranches: idBranches.flatMap(b => b.idBranches ?? [b.value]).filter(id => typeof id === 'string'),
      dynamic: {
        cdDynamic: cdDynamic.value,
        dsDynamic: cdDynamic.label
      },
      dsCard: dsCard?.value || '',
      dtStart: dateRange[0]?.format('DD-MM-YYYY'),
      dtEnd: dateRange[1]?.format('DD-MM-YYYY'),
      annualRepeat: repeatYearly,
      nuRange: ranges.length,
      status,
      parcelRangeList: ranges.map(r => ({
        cdParcel: r.cdParcel?.value,
        prMinValue: parseCurrency(r.prMinValue),
        prMaxValue: parseCurrency(r.prMaxValue),
        dynamic: r.dynamic ? {
          cdDynamic: r.dynamic.cdDynamic,
          dsDynamic: r.dynamic.dsDynamic
        } : null
      }))
    };

    onSave && onSave(payload);
  };


  return (
    <Modal
      title={title}
      size="lg"
      isOpen={open}
      onCloseModal={() => onOpenChange(false)}
      useControlledState
      className="pricebanner-modal"
      confirmButtonText={translate('save_button_label')}
      onConfirm={handleConfirm}
      noCloseOnConfirm
    >

      <div className="pricebanner-form-container">
        <Row>
          <Col md={6}>
            <RegionBranchMultipleSelect
              required
              label={translate('price.banner.stores')}
              placeholder={translate('select.one.store')}
              value={idBranches}
              onChange={e => {
                setIdBranches(e.target.value)
                setErrors(prev => ({ ...prev, branches: false }))
              }}
              validateValue={value => !!value && value.length > 0}
              errorText={errors.branches ? requiredMessage : undefined}
              showHelperArea={false}
              selectAll
              isMulti
              user={user}
              translate={translate}
              excludeInactive={true}
            />
          </Col>

          <Col md={6}>
            <DynamicSelectField
              value={cdDynamic}
              onChange={v => {
                setCdDynamic(v)
                setErrors(e => ({ ...e, dynamic: false }))
              }}
              required
              validateValue={value => !!value && !!value.value}
              errorText={errors.dynamic ? requiredMessage : undefined}
              showHelperArea={false}
              user={user}
              translate={translate}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <SelectField
              label={translate('config.pricebannerConfig.tableHeader.dsCard')}
              placeholder={translate('config.pricebannerConfig.form.dsCardLabel2')}
              value={dsCard}
              options={[{ label: 'Celebre', value: 'Celebre' }]}
              onChange={v => {
                setDsCard(v)
                setErrors(e => ({ ...e, cards: false }))
              }}
              required
              errorText={errors.cards ? requiredMessage : undefined}
              showHelperArea={false}
              validateValue={(value) => !!value?.value}
            />
          </Col>
          <Col md={6}>
            <DateIntervalInput
              label={translate('config.pricebannerConfig.form.dtPeriodLabel')}
              initialValue={dateRange}
              onChange={v => {
                setDateRange(v)
                setErrors(e => ({ ...e, date: false }))
              }}
              required
              validateValue={value => Array.isArray(value) && value.length === 2 && !!value[0] && !!value[1]}
              error={errors.date ? 'error' : undefined}
              errorMsg={errors.date ? requiredMessage : undefined}
              selectAll
              isMulti
              noSelect
              minDate={new Date()}

            />

            {!errors.date && (
              <Input
                id="inRepeatAnnually"
                type="checkbox"
                label={translate('scheduleBlock.inRepeatAnnually')}
                containerClassName="repeatYearly"
                checked={repeatYearly}
                onChange={(e) => setRepeatYearly(e.target.checked)}
              />
            )}
          </Col>
        </Row>
      </div>

      {ranges.map((range, index) => {
        const usedParcels = getUsedParcels(range.id);
        const parcelOptions = Array.from({ length: 40 }, (_, i) => {
          const value = (i + 1).toString();
          return { label: `${value}x`, value };
        }).filter(opt => !usedParcels.includes(opt.value));

        const previousMaxValue = getPreviousMaxValue(index);

        return (
          <ParcelRange
            key={range.id}
            title={`Faixa de parcelamento ${index + 1}`}
            onAddAbove={() => addRange('above', range.id)}
            onAddBelow={() => addRange('below', range.id)}
            canRemove={ranges.length > 1}
            onRemove={() => removeRange(range.id)}
            data={range}
            onChange={(data) => updateRange(range.id, data)}
            errors={errors.ranges?.[range.id] || {}}
            parcelOptions={parcelOptions}
            previousMaxValue={previousMaxValue}
            user={user}
            translate={translate}
          />
        )
      })}

    </Modal>
  );
}

PricebannerAddModal.propTypes = {
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  user: PropTypes.object,
  translate: PropTypes.func.isRequired,
  initialData: PropTypes.object
};
